import {
  type DocumentMeta,
  type DocumentVersion,
} from "@prismicio/editor-fields"
import { useRequest } from "@prismicio/editor-support/Suspense"
import {
  Box,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Text,
  useTour,
} from "@prismicio/editor-ui"
import { captureException } from "@sentry/react"
import { useCallback, useState } from "react"

import {
  getPreviewUrls,
  getRedirectURL,
  type PreviewUrl,
  refreshRelease,
} from "../../app/services/core"
import { trackPreviewOpen } from "../../app/services/tracking"
import { useDocument } from "../DocumentProvider"
import { useIsVersionInMigrationRelease } from "../documentRouting"

export function PreviewMenu() {
  const { data } = useDocument()

  const { results: previewOptions } = useRequest(getPreviewUrls, [])

  const [contentWidth, setContentWidth] = useState<number>()

  const { isRedirectUrlLoading, openRedirectUrl } = useOpenRedirectUrl()

  const tour = useTour()

  if (data.type === "new") return null
  const { meta, version } = data

  const buttonRefCallback = (button: HTMLButtonElement | null) => {
    if (!button) return
    setContentWidth(button.clientWidth)
  }

  function openPreview(previewOption: PreviewUrl) {
    trackPreviewOpen({
      label: previewOption.label,
      documentId: meta.id,
    })

    openRedirectUrl({
      previewOption,
      documentMeta: meta,
      documentVersion: version,
    })

    if (
      tour.step?.id === "full-page-preview" ||
      tour.step?.id === "reorder-your-slices-3"
    )
      tour.next()
  }

  return (
    <Box flexDirection="column">
      <DropdownMenu>
        <DropdownMenuTrigger>
          <Button
            ref={buttonRefCallback}
            color="grey"
            startIcon="visibility"
            size="large"
            loading={isRedirectUrlLoading}
            data-demo-id="preview-button"
          >
            Preview the page
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuLabel>
            {previewOptions.length > 0
              ? "Select preview"
              : "Preview not enabled"}
          </DropdownMenuLabel>
          <Box width={contentWidth} flexDirection="column">
            {previewOptions.map((option) => (
              <DropdownMenuItem
                key={option.id}
                onSelect={() => openPreview(option)}
              >
                <Text noWrap>{option.label}</Text>
              </DropdownMenuItem>
            ))}
          </Box>
        </DropdownMenuContent>
      </DropdownMenu>
    </Box>
  )
}

function useGetRedirectUrl() {
  const isVersionInMigrationRelease = useIsVersionInMigrationRelease()

  return useCallback(
    async (
      previewOption: PreviewUrl,
      meta: DocumentMeta,
      version: DocumentVersion,
    ) => {
      if (isVersionInMigrationRelease(version)) {
        await refreshRelease(version.release_id)
      }

      return getRedirectURL(previewOption.id, meta.id, version)
    },
    [isVersionInMigrationRelease],
  )
}

export function useOpenRedirectUrl() {
  const [isRedirectUrlLoading, setIsRedirectUrlLoading] = useState(false)
  const getRedirectUrl = useGetRedirectUrl()

  interface openRedirectUrlArgs {
    previewOption: PreviewUrl
    documentMeta: DocumentMeta
    documentVersion: DocumentVersion
  }

  function openRedirectUrl(args: openRedirectUrlArgs) {
    const { previewOption, documentMeta, documentVersion } = args

    setIsRedirectUrlLoading(true)

    getRedirectUrl(previewOption, documentMeta, documentVersion)
      .then((body) => {
        const previewUrl = new URL(body.preview_url)
        previewUrl.searchParams.set("session_id", body.session_id)
        window.open(previewUrl)
      })
      .catch(captureException)
      .finally(() => setIsRedirectUrlLoading(false))
  }

  return {
    isRedirectUrlLoading,
    openRedirectUrl,
  }
}
